import { useNavigate, useLocation } from 'react-router-dom';
import { Phone, Mail, MapPin, Facebook, Instagram } from 'lucide-react';
import { useLanguage } from '../i18n/LanguageContext';

const Footer = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useLanguage();

  const handleScrollLink = (hash) => {
    if (location.pathname !== '/') {
      navigate('/');
      setTimeout(() => {
        const element = document.getElementById(hash);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth' });
        }
      }, 300);
    } else {
      const element = document.getElementById(hash);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth' });
      }
    }
  };

  const handleLinkClick = (path) => {
    navigate(path);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <footer className="bg-[#1D4E41] text-white">
      <div className="container mx-auto px-4 md:px-6 max-w-7xl py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-10">
          {/* Brand */}
          <div>
            <div className="mb-6 flex items-center gap-3">
              <img 
                src="https://customer-assets.emergentagent.com/job_a1eb42dc-1756-4f7a-b3c1-591c05407e32/artifacts/udsnjmd2_1000353198.png" 
                alt="Travel Guide Costa Rica" 
                className="h-[72px] w-auto rounded-lg"
              />
              <img 
                src="/ctp-ict-logos.png" 
                alt="CTP - Consejo de Transporte Público | ICT - Costa Rica Tourist Board" 
                className="h-[72px] w-auto rounded-md bg-white px-3 py-1"
              />
            </div>
            <p className="text-white/70 leading-relaxed">
              {t('footer.description')}
            </p>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="font-semibold text-lg mb-6">{t('footer.quickLinks')}</h4>
            <ul className="space-y-3">
              <li>
                <button onClick={() => handleLinkClick('/')} className="text-white/70 hover:text-white transition-colors">{t('footer.home')}</button>
              </li>
              <li>
                <button onClick={() => handleLinkClick('/tours')} className="text-white/70 hover:text-white transition-colors">{t('footer.toursTransfers')}</button>
              </li>
              <li>
                <button onClick={() => handleScrollLink('about')} className="text-white/70 hover:text-white transition-colors">{t('footer.aboutUs')}</button>
              </li>
              <li>
                <button onClick={() => handleScrollLink('reviews')} className="text-white/70 hover:text-white transition-colors">{t('footer.reviews')}</button>
              </li>
              <li>
                <button onClick={() => handleScrollLink('contact')} className="text-white/70 hover:text-white transition-colors">{t('footer.contact')}</button>
              </li>
              <li>
                <button onClick={() => handleLinkClick('/cancellation-policy')} className="text-white/70 hover:text-white transition-colors">{t('footer.cancellation')}</button>
              </li>
              <li>
                <button onClick={() => handleLinkClick('/environmental-policy')} className="text-white/70 hover:text-white transition-colors">{t('footer.environmental')}</button>
              </li>
              <li>
                <button onClick={() => handleLinkClick('/safety-information')} className="text-white/70 hover:text-white transition-colors">{t('footer.safety')}</button>
              </li>
              <li>
                <button onClick={() => handleLinkClick('/community-support')} className="text-white/70 hover:text-white transition-colors">{t('footer.community')}</button>
              </li>
            </ul>
          </div>

          {/* Contact */}
          <div>
            <h4 className="font-semibold text-lg mb-6">{t('footer.contactUs')}</h4>
            <ul className="space-y-4">
              <li>
                <a href="tel:+50688173167" className="flex items-center gap-3 text-white/70 hover:text-white transition-colors">
                  <Phone className="w-5 h-5" />
                  +506 8817-3167
                </a>
              </li>
              <li>
                <a href="mailto:reservations@travelguide.cr" className="flex items-center gap-3 text-white/70 hover:text-white transition-colors">
                  <Mail className="w-5 h-5" />
                  reservations@travelguide.cr
                </a>
              </li>
              <li>
                <div className="flex items-center gap-3 text-white/70">
                  <MapPin className="w-5 h-5" />
                  {t('footer.location')}
                </div>
              </li>
            </ul>
          </div>

          {/* Social */}
          <div>
            <h4 className="font-semibold text-lg mb-6">{t('footer.followUs')}</h4>
            <div className="flex gap-4">
              <a
                href="https://facebook.com/share/19APSEV9tP/"
                target="_blank"
                rel="noopener noreferrer"
                className="w-10 h-10 bg-white/10 rounded-full flex items-center justify-center hover:bg-white/20 transition-colors"
                data-testid="social-facebook"
              >
                <Facebook className="w-5 h-5" />
              </a>
              <a
                href="https://instagram.com/erickcamachom"
                target="_blank"
                rel="noopener noreferrer"
                className="w-10 h-10 bg-white/10 rounded-full flex items-center justify-center hover:bg-white/20 transition-colors"
                data-testid="social-instagram"
              >
                <Instagram className="w-5 h-5" />
              </a>
              <a
                href="https://tripadvisor.com/Attraction_Review-g8627695-d13437753-Reviews-Travel_Guide_Costa_Rica-San_Pedro_de_Poas_Province_of_Alajuela.html"
                target="_blank"
                rel="noopener noreferrer"
                className="w-10 h-10 bg-white/10 rounded-full flex items-center justify-center hover:bg-white/20 transition-colors"
                data-testid="social-tripadvisor"
              >
                <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M12.006 4.295c-2.67 0-5.338.784-7.645 2.353H0l1.963 2.135a5.997 5.997 0 0 0 4.04 10.43 5.976 5.976 0 0 0 4.075-1.6L12 19.705l1.922-2.09a5.972 5.972 0 0 0 4.072 1.598 6 6 0 0 0 4.041-10.43L24 6.648h-4.35a13.573 13.573 0 0 0-7.644-2.353zM12 6.255c1.531 0 3.063.303 4.504.903C13.943 8.138 12 10.43 12 13.1c0-2.671-1.942-4.962-4.504-5.942A11.72 11.72 0 0 1 12 6.256zM6.002 9.157a4.059 4.059 0 1 1 0 8.118 4.059 4.059 0 0 1 0-8.118zm11.992 0a4.059 4.059 0 1 1 .002 8.118 4.059 4.059 0 0 1-.002-8.118zM6.002 11.15a2.063 2.063 0 1 0 0 4.125 2.063 2.063 0 0 0 0-4.125zm11.992 0a2.066 2.066 0 1 0 0 4.131 2.066 2.066 0 0 0 0-4.13z"/>
                </svg>
              </a>
            </div>
            <p className="mt-6 text-white/50 text-sm">
              {t('footer.excellence')}
            </p>
          </div>
        </div>

        <div className="border-t border-white/10 mt-12 pt-8 text-center text-white/50 text-sm">
          <p>&copy; {new Date().getFullYear()} Travel Guide Costa Rica. {t('footer.allRights')}</p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
